import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

export type Lang = "en" | "ar";

const KEY = "bookswap_lang";

type Dict = Record<string, string>;

const en: Dict = {
  // nav
  "nav.menu": "Menu",
  "nav.browse": "Browse books",
  "nav.add": "Add a book",
  "nav.my_books": "My books",
  "nav.messages": "Messages",
  "nav.profile": "Profile",
  "nav.leaderboard": "Leaderboard",
  "nav.recommendations": "For you",
  "nav.signin": "Sign in",
  "nav.signout": "Sign out",
  "nav.get_started": "Get Started",
  "nav.language": "Language",
  // browse
  "browse.title": "Browse books",
  "browse.subtitle_all": "All listings from the community",
  "browse.subtitle_nearby": "Within {km} km of your location",
  "browse.use_location": "Use my location (optional)",
  "browse.showing_nearby": "Showing nearby",
  "browse.show_nearby": "Show only nearby",
  "browse.list_view": "List View",
  "browse.map_view": "Map View",
  "browse.search_ph": "Search title or author…",
  "browse.cat_all": "All categories",
  "browse.age_all": "All ages",
  "browse.empty_title": "No books match yet",
  "browse.empty_sub": "Try clearing filters or check back soon.",
  "browse.loading": "Loading books…",
  "browse.share_loc": "Share your location to see books near you",
  "browse.within": "{n} {label} available within 1 km of you",
  "browse.filters_active": "{n} filter active",
  "browse.filters_active_plural": "{n} filters active",
  "browse.clear_filters": "Clear filters",
  "browse.guest_cta": "Join free to message owners and list your own books.",
  "browse.guest_cta_btn": "Get started",
  // home story
  "home.story.title": "Why we built Book Swap",
  "home.story.p1": "Books shouldn't sit on shelves collecting dust when a neighbor would love to read them.",
  "home.story.p2": "Book Swap turns every street into a tiny library — no shipping, no fees, just readers passing good stories along.",
  "home.story.p3": "Lend the novel that changed your year. Borrow the cookbook three doors down. Build a reading habit with the people who actually live near you.",
  // leaderboard
  "lb.title": "Community Leaderboard",
  "lb.subtitle": "Top sharers in your community, ranked by trust score.",
  "lb.rank": "Rank",
  "lb.member": "Member",
  "lb.listed": "Listed",
  "lb.exchanges": "Exchanges",
  "lb.score": "Trust Score",
  "lb.empty": "No members on the board yet — be the first!",
  // recs
  "recs.title": "Picked for you",
  "recs.subtitle": "Books from neighbors, matched to the categories you already share.",
  "recs.refresh": "Refresh",
  "recs.empty": "List a few books so we can suggest matching reads from your neighbors.",
  "recs.error": "Could not load recommendations right now.",
  "recs.why": "Why",
  "recs.no_books": "No matching books available right now. Check back soon.",
  // profile
  "profile.trust": "Trust score",
  "profile.exchanges_done": "Exchanges completed",
  // home
  "home.badge": "Local exchange · No shipping · 100% free",
  "home.h1.a": "A cozy library",
  "home.h1.b": "on every street.",
  "home.sub": "Lend, borrow, and gift books with neighbors. Meet readers nearby, build a shelf, and pass good stories along — all free.",
  "home.cta_start": "Start swapping",
  "home.cta_browse": "Browse books near me",
  "home.stat.books": "books listed",
  "home.stat.readers": "readers",
  "home.stat.libraries": "street libraries",
  "home.highlight.fast": "List in 30s",
  "home.highlight.meet": "Meet neighbors",
  "home.highlight.free": "Always free",
  "browse.empty_first_title": "No books on this block yet",
  "browse.empty_first_sub": "Be the first to share a story with your neighborhood.",
  "browse.empty_first_cta": "List the first book",
  "home.how.title": "How Book Swap works",
  "home.how.sub": "Three steps. Zero shipping. Pure neighborhood magic.",
  "home.how.list_t": "List your books",
  "home.how.list_d": "Add a cover, set the loan length, and pick a category. Done in 30 seconds.",
  "home.how.swap_t": "Swap and return",
  "home.how.swap_d": "Offer one of yours in trade. Meet up, hand it over, and pass the story along.",
  "home.how.browse_t": "Browse and discover",
  "home.how.browse_d": "Search books listed near you, request a swap, and arrange a meetup. Your next read is just down the street.",
  "home.more.title": "More than swapping",
  "home.more.sub": "A real community for readers, families, and street librarians.",
  "home.feat.qr_t": "Reading journey",
  "home.feat.qr_d": "Track what you're reading, mark finished books, and rate every story along the way.",
  "home.feat.clubs_t": "Book clubs",
  "home.feat.clubs_d": "Join or host reading clubs around your favorite books and meet fellow readers.",
  "home.feat.donate_t": "Donations",
  "home.feat.donate_d": "Mark books as gifts. No swap required — pure pay-it-forward.",
  "home.feat.recs_t": "Books recommended for you",
  "home.feat.recs_d": "Personalized picks based on your shelf and exchange history.",
  "home.feat.badges_t": "Badges & trust",
  "home.feat.badges_d": "Complete reading missions and build your reader reputation.",
  "home.feat.family_t": "Your bookshelves",
  "home.feat.family_d": "Organize your books into custom shelves — by mood, genre, or who they're for.",
  "home.cta.title": "Join your neighborhood library",
  "home.cta.sub": "It's free, friendly, and the books are already on your block.",
  "home.cta.btn": "Create your free account",
  "home.footer": "© 2026 Book Swap. Built for neighbors.",
  "home.footer.terms": "Terms of use",
};

const ar: Dict = {
  "nav.menu": "القائمة",
  "nav.browse": "تصفح الكتب",
  "nav.add": "إضافة كتاب",
  "nav.my_books": "كتبي",
  "nav.messages": "الرسائل",
  "nav.profile": "الملف الشخصي",
  "nav.leaderboard": "لوحة المتصدرين",
  "nav.recommendations": "مقترحاتك",
  "nav.signin": "تسجيل الدخول",
  "nav.signout": "تسجيل الخروج",
  "nav.get_started": "ابدأ الآن",
  "nav.language": "اللغة",

  "browse.title": "تصفح الكتب",
  "browse.subtitle_all": "جميع الكتب المعروضة في المجتمع",
  "browse.subtitle_nearby": "ضمن {km} كم من موقعك",
  "browse.use_location": "استخدم موقعي (اختياري)",
  "browse.showing_nearby": "عرض القريبة",
  "browse.show_nearby": "عرض القريبة فقط",
  "browse.list_view": "عرض قائمة",
  "browse.map_view": "عرض خريطة",
  "browse.search_ph": "ابحث عن عنوان أو مؤلف…",
  "browse.cat_all": "كل التصنيفات",
  "browse.age_all": "كل الأعمار",
  "browse.empty_title": "لا توجد نتائج",
  "browse.empty_sub": "جرّب إزالة الفلاتر أو عُد لاحقًا.",
  "browse.loading": "جارٍ تحميل الكتب…",
  "browse.share_loc": "شارك موقعك لرؤية الكتب القريبة منك",
  "browse.within": "{n} {label} متاحة على بُعد 1 كم منك",
  "browse.filters_active": "فلتر واحد مفعّل",
  "browse.filters_active_plural": "{n} فلاتر مفعّلة",
  "browse.clear_filters": "مسح الفلاتر",
  "browse.guest_cta": "انضم مجانًا لمراسلة المالكين وعرض كتبك.",
  "browse.guest_cta_btn": "ابدأ الآن",

  "home.story.title": "لماذا أنشأنا Book Swap",
  "home.story.p1": "لا ينبغي أن تبقى الكتب على الرفوف يعلوها الغبار بينما يودّ جار قراءتها.",
  "home.story.p2": "يحوّل Book Swap كل شارع إلى مكتبة صغيرة — بدون شحن ولا رسوم، فقط قرّاء يتبادلون القصص الجميلة.",
  "home.story.p3": "أعِر الرواية التي غيّرت عامك. استعِر كتاب الطبخ من الشقة المجاورة. ابنِ عادة قراءة مع من يسكنون فعلاً قريبًا منك.",

  "lb.title": "لوحة متصدري المجتمع",
  "lb.subtitle": "أفضل المشاركين في مجتمعك، مرتبين حسب درجة الثقة.",
  "lb.rank": "الترتيب",
  "lb.member": "العضو",
  "lb.listed": "معروض",
  "lb.exchanges": "المبادلات",
  "lb.score": "درجة الثقة",
  "lb.empty": "لا يوجد أعضاء بعد — كن الأول!",

  "recs.title": "مختارة لك",
  "recs.subtitle": "كتب من الجيران تطابق التصنيفات التي تشاركها بالفعل.",
  "recs.refresh": "تحديث",
  "recs.empty": "اعرض بضعة كتب لنقترح عليك قراءات مطابقة من جيرانك.",
  "recs.error": "تعذّر تحميل التوصيات الآن.",
  "recs.why": "السبب",
  "recs.no_books": "لا توجد كتب مطابقة حاليًا. عُد قريبًا.",

  "profile.trust": "درجة الثقة",
  "profile.exchanges_done": "المبادلات المكتملة",

  "home.badge": "تبادل محلي · بدون شحن · مجاني 100٪",
  "home.h1.a": "مكتبة دافئة",
  "home.h1.b": "في كل شارع.",
  "home.sub": "استعِر، أعِر، وأهدِ الكتب مع جيرانك. تعرّف على قرّاء قريبين منك، كوّن مكتبتك، ومرّر القصص الجميلة — كل ذلك مجانًا.",
  "home.cta_start": "ابدأ التبادل",
  "home.cta_browse": "تصفح الكتب القريبة",
  "home.stat.books": "كتاب معروض",
  "home.stat.readers": "قارئ",
  "home.stat.libraries": "مكتبة شارع",
  "home.highlight.fast": "إدراج خلال 30 ثانية",
  "home.highlight.meet": "تعرّف على جيرانك",
  "home.highlight.free": "مجاني دائمًا",
  "browse.empty_first_title": "لا توجد كتب في حيك بعد",
  "browse.empty_first_sub": "كن أول من يشارك قصة مع جيرانه.",
  "browse.empty_first_cta": "أدرج أول كتاب",
  "home.how.title": "كيف يعمل Book Swap",
  "home.how.sub": "ثلاث خطوات. بدون شحن. سحر الحي بأكمله.",
  "home.how.list_t": "اعرض كتبك",
  "home.how.list_d": "أضف غلافًا، حدّد مدة الإعارة، واختر التصنيف. ينتهي خلال 30 ثانية.",
  "home.how.swap_t": "بادل وأعِد",
  "home.how.swap_d": "اعرض كتابًا من عندك للتبادل. التقِ بالطرف الآخر، سلّمه الكتاب، ومرّر القصة.",
  "home.how.browse_t": "تصفّح واكتشف",
  "home.how.browse_d": "ابحث عن الكتب المعروضة بقربك، اطلب التبادل، ورتّب اللقاء. قراءتك القادمة على بُعد خطوات.",
  "home.more.title": "أكثر من مجرد تبادل",
  "home.more.sub": "مجتمع حقيقي للقرّاء والعائلات وأمناء مكتبات الشارع.",
  "home.feat.qr_t": "رحلة القراءة",
  "home.feat.qr_d": "تابع ما تقرأه، وضع الكتب المنتهية، وقيّم كل قصة على طول الطريق.",
  "home.feat.clubs_t": "أندية القراءة",
  "home.feat.clubs_d": "انضم أو استضف أندية قراءة حول كتبك المفضلة وتعرّف على قرّاء آخرين.",
  "home.feat.donate_t": "التبرعات",
  "home.feat.donate_d": "ضع كتبًا كهدايا. لا حاجة للتبادل — عطاء خالص.",
  "home.feat.recs_t": "كتب مقترحة لك",
  "home.feat.recs_d": "اختيارات مخصصة بناءً على مكتبتك وسجل تبادلاتك.",
  "home.feat.badges_t": "الشارات والثقة",
  "home.feat.badges_d": "أكمل المهام، تصدّر اللوحة، وابنِ سمعتك كقارئ.",
  "home.feat.family_t": "أرففك للكتب",
  "home.feat.family_d": "نظّم كتبك في أرفف مخصصة — حسب المزاج أو النوع أو لمن هي.",
  "home.cta.title": "انضم إلى مكتبة حيّك",
  "home.cta.sub": "مجاني، ودود، والكتب موجودة بالفعل في شارعك.",
  "home.cta.btn": "أنشئ حسابك المجاني",
  "home.footer": "© 2026 Book Swap. مصمَّم للجيران.",
  "home.footer.terms": "شروط الاستخدام",
};

const dicts: Record<Lang, Dict> = { en, ar };

export const optionTranslations: Record<string, string> = {
  "Fiction": "روايات",
  "Non-Fiction": "كتب واقعية",
  "Mystery & Thriller": "غموض وتشويق",
  "Sci-Fi & Fantasy": "خيال علمي وفانتازيا",
  "Romance": "رومانسي",
  "Biography": "سير ذاتية",
  "History": "تاريخ",
  "Science": "علوم",
  "Self-Help": "تطوير الذات",
  "Children's": "أطفال",
  "Young Adult": "يافعون",
  "Comics & Graphic Novels": "قصص مصورة وروايات جرافيكية",
  "Poetry": "شعر",
  "Religious": "ديني",
  "Other": "أخرى",
  "Children (0-12)": "أطفال (0-12)",
  "Teen (13-17)": "مراهقون (13-17)",
  "Young Adult (18-25)": "يافعون (18-25)",
  "Adult (26-50)": "بالغون (26-50)",
  "Senior (50+)": "كبار السن (50+)",
  "All Ages": "كل الأعمار",
  "1 Day": "يوم واحد",
  "3 Days": "3 أيام",
  "1 Week": "أسبوع واحد",
  "2 Weeks": "أسبوعان",
  "1 Month": "شهر واحد",
  "3 Months": "3 أشهر",
  "6 Months": "6 أشهر",
  "Like New": "كالجديد",
  "Good": "جيد",
  "Fair": "مقبول",
  "Worn": "مستهلك",
  "available": "متاح",
  "pending": "قيد الانتظار",
  "reserved": "محجوز",
  "accepted": "مقبول",
  "declined": "مرفوض",
  "swapped": "تم التبادل",
  "exchanged": "تم التبادل",
};

const uiTextAr: Dict = {
  ...optionTranslations,
  "404": "404",
  "This page doesn't exist.": "هذه الصفحة غير موجودة.",
  "Go home": "العودة للرئيسية",
  "Something went wrong": "حدث خطأ ما",
  "An unexpected error occurred. Please try again.": "حدث خطأ غير متوقع. يرجى المحاولة مرة أخرى.",
  "Try again": "حاول مرة أخرى",
  "English": "English",
  "Book Swap": "Book Swap",
  "Browse books": "تصفح الكتب",
  "Add a book": "إضافة كتاب",
  "My books": "كتبي",
  "Messages": "الرسائل",
  "Profile": "الملف الشخصي",
  "Leaderboard": "لوحة المتصدرين",
  "For you": "مقترحاتك",
  "Community libraries": "مكتبات المجتمع",
  "Family shelves": "مكتبات العائلة",
  "Reading missions": "مهام القراءة",
  "Reading clubs": "أندية القراءة",
  "Activity feed": "النشاطات",
  "My reading journey": "رحلتي القرائية",
  "Invite a friend": "ادعُ صديقًا",
  "Sign in": "تسجيل الدخول",
  "Sign out": "تسجيل الخروج",
  "Get Started": "ابدأ الآن",
  "Menu": "القائمة",
  "Language": "اللغة",
  "All listings from the community": "جميع الكتب المعروضة في المجتمع",
  "Use my location": "استخدم موقعي",
  "Use my location (optional)": "استخدم موقعي (اختياري)",
  "Showing nearby": "عرض القريبة",
  "Show only nearby": "عرض القريبة فقط",
  "List View": "عرض قائمة",
  "Map View": "عرض خريطة",
  "Showing donations only": "عرض التبرعات فقط",
  "Donations only": "التبرعات فقط",
  "Search radius:": "نطاق البحث:",
  "Search title or author…": "ابحث عن عنوان أو مؤلف…",
  "All categories": "كل التصنيفات",
  "All ages": "كل الأعمار",
  "Loading books…": "جارٍ تحميل الكتب…",
  "No books match yet": "لا توجد نتائج",
  "Try clearing filters or check back soon.": "جرّب إزالة الفلاتر أو عُد لاحقًا.",
  "No books match your filters.": "لا توجد كتب تطابق الفلاتر.",
  "Share your location to see books near you": "شارك موقعك لرؤية الكتب القريبة منك",
  "Book not found.": "الكتاب غير موجود.",
  "This book isn't available.": "هذا الكتاب غير متاح.",
  "📷 Sending your borrow request…": "📷 جارٍ إرسال طلب الاستعارة…",
  "List a book": "اعرض كتابًا",
  "Share a book with neighbors. They'll borrow, read, and bring it back.": "شارك كتابًا مع جيرانك. سيستعيرونه ويقرؤونه ثم يعيدونه.",
  "Cover image": "صورة الغلاف",
  "Title *": "العنوان *",
  "Author *": "المؤلف *",
  "ISBN (optional)": "ISBN (اختياري)",
  "Category *": "التصنيف *",
  "Age group *": "الفئة العمرية *",
  "Exchange duration *": "مدة التبادل *",
  "Condition *": "الحالة *",
  "Description": "الوصف",
  "Contact number (optional)": "رقم التواصل (اختياري)",
  "Optional. Readers can also message you in the app — no phone needed.": "اختياري. يمكن للقراء مراسلتك داخل التطبيق أيضًا — لا حاجة للهاتف.",
  "💝 Donate this book": "💝 تبرع بهذا الكتاب",
  "When ON, you're giving the book away — no return expected.": "عند التفعيل، أنت تهدي الكتاب — لا يُتوقع إرجاعه.",
  "Drop-off at a community library (optional)": "التسليم في مكتبة مجتمعية (اختياري)",
  "No library": "بدون مكتبة",
  "Choose…": "اختر…",
  "Listing…": "جارٍ الإدراج…",
  "List my book": "اعرض كتابي",
  "Your book is now listed!": "تم عرض كتابك الآن!",
  "Could not list book. Please try again.": "تعذّر عرض الكتاب. يرجى المحاولة مرة أخرى.",
  "Back to browse": "العودة للتصفح",
  "← Back to browse": "← العودة للتصفح",
  "Listed by": "أدرجه",
  "A neighbor": "جار",
  "Message owner": "راسل المالك",
  "Sign in to message owner": "سجّل الدخول لمراسلة المالك",
  "Report listing": "الإبلاغ عن الإعلان",
  "Report this listing": "الإبلاغ عن هذا الإعلان",
  "Book was damaged": "الكتاب تضرر",
  "Did not return the book (instant ban)": "لم يُعد الكتاب (حظر فوري)",
  "Other issue": "مشكلة أخرى",
  "What's the issue? (optional)": "ما المشكلة؟ (اختياري)",
  "Cancel": "إلغاء",
  "Submitting…": "جارٍ الإرسال…",
  "Submit report": "إرسال البلاغ",
  "Could not submit report.": "تعذّر إرسال البلاغ.",
  "Report submitted. Thank you.": "تم إرسال البلاغ. شكرًا لك.",
  "Report submitted. A strike was added to this account.": "تم إرسال البلاغ. أُضيفت مخالفة إلى هذا الحساب.",
  "Request this book": "اطلب هذا الكتاب",
  "Optionally offer one of your books in trade.": "يمكنك اختياريًا عرض أحد كتبك للتبادل.",
  "Offer a book in return (optional)": "اعرض كتابًا بالمقابل (اختياري)",
  "No books to offer yet": "لا توجد كتب لعرضها بعد",
  "Add a friendly note (optional)": "أضف ملاحظة ودية (اختياري)",
  "Sending…": "جارٍ الإرسال…",
  "Send exchange request": "إرسال طلب تبادل",
  "Request sent! The owner will be in touch.": "تم إرسال الطلب! سيتواصل معك المالك.",
  "Could not send your request.": "تعذّر إرسال طلبك.",
  "QR borrow tag": "بطاقة استعارة QR",
  "Print or share this code. Anyone who scans it sends you a borrow request instantly.": "اطبع أو شارك هذا الرمز. من يمسحه يرسل طلب استعارة فورًا.",
  "This is your listing. Manage it from My Books.": "هذا إعلانك. أدِره من صفحة كتبي.",
  "Add to my journey": "أضف إلى رحلتي",
  "Added to your reading journey 📖": "أُضيف إلى رحلتك القرائية 📖",
  "Could not add.": "تعذّرت الإضافة.",
  "Loading…": "جارٍ التحميل…",
  "Welcome back": "مرحبًا بعودتك",
  "Sign in to browse books near you.": "سجّل الدخول لتصفح الكتب القريبة منك.",
  "Email": "البريد الإلكتروني",
  "Password": "كلمة المرور",
  "Signing in…": "جارٍ تسجيل الدخول…",
  "Welcome back!": "مرحبًا بعودتك!",
  "or": "أو",
  "New here?": "جديد هنا؟",
  "Create an account": "إنشاء حساب",
  "Invalid email or password.": "البريد الإلكتروني أو كلمة المرور غير صحيحة.",
  "Join Book Swap": "انضم إلى Book Swap",
  "Free forever. Books just down the street.": "مجاني دائمًا. كتب قريبة منك في الشارع.",
  "Full name *": "الاسم الكامل *",
  "Mobile number *": "رقم الجوال *",
  "Short address *": "العنوان المختصر *",
  "Location *": "الموقع *",
  "Use my current location": "استخدم موقعي الحالي",
  "Getting location…": "جارٍ تحديد الموقع…",
  "Location captured ✓": "تم حفظ الموقع ✓",
  "Location captured.": "تم حفظ الموقع.",
  "Required to show you books nearby.": "مطلوب لعرض الكتب القريبة منك.",
  "Interested in which type of books?": "ما أنواع الكتب التي تهمك؟",
  "Password *": "كلمة المرور *",
  "At least 8 characters.": "8 أحرف على الأقل.",
  "Creating account…": "جارٍ إنشاء الحساب…",
  "Create account": "إنشاء حساب",
  "Already a member?": "لديك حساب؟",
  "Account created! Welcome to Book Swap.": "تم إنشاء الحساب! مرحبًا بك في Book Swap.",
  "Full name is required.": "الاسم الكامل مطلوب.",
  "Mobile number is required.": "رقم الجوال مطلوب.",
  "Short address is required.": "العنوان المختصر مطلوب.",
  "Could not create your account. The email may already be in use.": "تعذّر إنشاء حسابك. قد يكون البريد مستخدمًا بالفعل.",
  "Could not get your location. Please allow location access.": "تعذّر تحديد موقعك. يرجى السماح بالوصول للموقع.",
  "No listings yet": "لا توجد إعلانات بعد",
  "List your first book — it takes 30 seconds.": "اعرض كتابك الأول — يستغرق ذلك 30 ثانية.",
  "A reader": "قارئ",
  "Requests": "الطلبات",
  "Status:": "الحالة:",
  "Accept": "قبول",
  "Decline": "رفض",
  "Listing removed": "تم حذف الإعلان",
  "Could not remove this listing.": "تعذّر حذف هذا الإعلان.",
  "Could not update this request.": "تعذّر تحديث هذا الطلب.",
  "Anonymous": "مجهول",
  "No members on the board yet — be the first!": "لا يوجد أعضاء بعد — كن الأول!",
  "Trust Score": "درجة الثقة",
  "Rank": "الترتيب",
  "Member": "العضو",
  "Listed": "معروض",
  "Exchanges": "المبادلات",
  "Community Leaderboard": "لوحة متصدري المجتمع",
  "Top sharers in your community, ranked by trust score.": "أفضل المشاركين في مجتمعك، مرتبين حسب درجة الثقة.",
  "Trust": "الثقة",
  "Verified": "موثّق",
  "swaps": "مبادلات",
  "reply rate": "معدل الرد",
  "Donation": "تبرع",
  "Swapped": "تم التبادل",
  "Reserved": "محجوز",
  "Not available": "غير متاح",
  "Already swapped": "تم تبادله بالفعل",
  "Not available right now": "غير متاح الآن",
  "Very close": "قريب جدًا",
  "Add to wishlist": "إضافة إلى المفضلة",
  "Remove from wishlist": "إزالة من المفضلة",
  "Sign in to save books to your wishlist.": "سجّل الدخول لحفظ الكتب في مفضلتك.",
  "Removed from wishlist": "تمت الإزالة من المفضلة",
  "Saved to wishlist 💗": "تم الحفظ في المفضلة 💗",
  "View Book": "عرض الكتاب",
  "No notifications yet.": "لا توجد إشعارات بعد.",
  "Help us improve Book Swap 📚": "ساعدنا في تحسين Book Swap 📚",
  "A quick review goes a long way. We'll only ask once if you submit.": "رأيك السريع مهم جدًا. سنسألك مرة واحدة فقط إذا أرسلت ملاحظاتك.",
  "What should we add?": "ما الذي يجب أن نضيفه؟",
  "What should we remove or fix?": "ما الذي يجب إزالته أو إصلاحه؟",
  "Anything else?": "أي شيء آخر؟",
  "Features, categories, ideas…": "ميزات، تصنيفات، أفكار…",
  "Anything that bothers you…": "أي شيء يزعجك…",
  "Optional": "اختياري",
  "Maybe later": "ربما لاحقًا",
  "Send feedback": "إرسال الملاحظات",
  "Please share at least one suggestion.": "يرجى مشاركة اقتراح واحد على الأقل.",
  "Could not submit. Try again.": "تعذّر الإرسال. حاول مرة أخرى.",
  "Thank you for your feedback! 💜": "شكرًا على ملاحظاتك! 💜",
  "Rate the app": "قيّم التطبيق",
  "This is about Book Swap itself — not about other readers. Your notes go straight to the team.": "هذا التقييم عن تطبيق Book Swap نفسه — وليس عن القرّاء الآخرين. ملاحظاتك تصل مباشرة إلى الفريق.",
  "Overall rating": "التقييم العام",
  "What did you like?": "ما الذي أعجبك؟",
  "What could be improved?": "ما الذي يمكن تحسينه؟",
  "The parts that worked well for you…": "الأشياء التي أعجبتك…",
  "Anything confusing, slow, or missing…": "أي شيء مربك أو بطيء أو ناقص…",
  "Please give a rating or share a comment.": "يرجى إعطاء تقييم أو كتابة ملاحظة.",
  "Sign in to rate the app": "سجّل الدخول لتقييم التطبيق",
  "Read the same book together — share thoughts, meet up, swap when done.": "اقرؤوا الكتاب نفسه معًا — شاركوا الآراء، التقوا، وتبادلوا عند الانتهاء.",
  "Start a new club": "ابدأ ناديًا جديدًا",
  "Club name (e.g. Sunset Sci-Fi)": "اسم النادي (مثال: خيال علمي عند الغروب)",
  "What's this club about?": "ما موضوع هذا النادي؟",
  "Creating…": "جارٍ الإنشاء…",
  "Create club": "إنشاء نادي",
  "All clubs": "كل الأندية",
  "No clubs yet — be the first to start one.": "لا توجد أندية بعد — كن أول من يبدأ ناديًا.",
  "Leave": "مغادرة",
  "Join": "انضمام",
  "View host": "عرض المضيف",
  "Give your club a name.": "أعطِ ناديك اسمًا.",
  "Club created": "تم إنشاء النادي",
  "Could not create club.": "تعذّر إنشاء النادي.",
  "Community Libraries": "مكتبات المجتمع",
  "Public drop-off spots where neighbors share books.": "نقاط تسليم عامة يشارك فيها الجيران الكتب.",
  "Host a library": "استضف مكتبة",
  "Host a community library": "استضافة مكتبة مجتمعية",
  "Name *": "الاسم *",
  "Address": "العنوان",
  "Street, area, city": "الشارع، المنطقة، المدينة",
  "Hours, what to bring, etc.": "الساعات، ما يجب إحضاره، إلخ.",
  "Create": "إنشاء",
  "No community libraries yet. Be the first host!": "لا توجد مكتبات مجتمعية بعد. كن أول مضيف!",
  "Library created!": "تم إنشاء المكتبة!",
  "Could not create library.": "تعذّر إنشاء المكتبة.",
  "Family Shelves": "مكتبات العائلة",
  "Curated collections shared by the community.": "مجموعات مختارة يشاركها المجتمع.",
  "New shelf": "مكتبة جديدة",
  "Create a shelf": "إنشاء مكتبة",
  "Emoji": "رمز تعبيري",
  "Create shelf": "إنشاء مكتبة",
  "No shelves yet. Create the first one!": "لا توجد مكتبات بعد. أنشئ الأولى!",
  "Shelf created!": "تم إنشاء المكتبة!",
  "Could not create shelf.": "تعذّر إنشاء المكتبة.",
  "e.g. Bedtime Stories": "مثال: قصص قبل النوم",
  "Your profile": "ملفك الشخصي",
  "Completed swaps": "المبادلات المكتملة",
  "Reply rate": "معدل الرد",
  "Books listed": "الكتب المعروضة",
  "Member since": "عضو منذ",
  "Full name": "الاسم الكامل",
  "Location or short address *": "الموقع أو العنوان المختصر *",
  "Short bio": "نبذة قصيرة",
  "Favorite quote": "اقتباس مفضل",
  "Your age group": "فئتك العمرية",
  "Save changes": "حفظ التغييرات",
  "Saving…": "جارٍ الحفظ…",
  "Profile saved": "تم حفظ الملف الشخصي",
  "Could not save your profile.": "تعذّر حفظ ملفك الشخصي.",
  "No conversations yet. Open a book and tap \"Message owner\".": "لا توجد محادثات بعد. افتح كتابًا واضغط \"راسل المالك\".",
  "New conversation": "محادثة جديدة",
  "Pick a conversation to start chatting.": "اختر محادثة لبدء الدردشة.",
  "Say hi 👋": "قل مرحبًا 👋",
  "Write a message…": "اكتب رسالة…",
  "Could not send message.": "تعذّر إرسال الرسالة.",
  "Reading Missions": "مهام القراءة",
  "EARNED": "مكتسبة",
  "Continue with Google": "المتابعة باستخدام Google",
  "Continue with Apple": "المتابعة باستخدام Apple",
  "Continue with Microsoft": "المتابعة باستخدام Microsoft",
  "We'll ask for your name, phone and location after sign-in.": "سنطلب اسمك ورقم هاتفك وموقعك بعد تسجيل الدخول.",
  "Email *": "البريد الإلكتروني *",
  "Phone number *": "رقم الجوال *",
  "+1 555 123 4567": "+1 555 123 4567",
  "Neighborhood, city": "الحي، المدينة",
  "Required to list books near you.": "مطلوب لعرض الكتب القريبة منك.",
  "What kinds of books interest you?": "ما أنواع الكتب التي تهمك؟",
  "Toggle theme": "تبديل السمة",
  "Notifications": "الإشعارات",
};

export function translateStaticText(value: string, lang: Lang): string {
  if (lang !== "ar") return value;
  const direct = uiTextAr[value] ?? uiTextAr[value.trim()];
  if (direct) return direct;
  let s = value;
  s = s.replace(/^Within (\d+) km of your location$/, "ضمن $1 كم من موقعك");
  s = s.replace(/^📚 (\d+) (book|books) available within (\d+) km of you$/, "📚 $1 كتاب متاح ضمن $3 كم منك");
  s = s.replace(/^📚 Share your location to see books near you$/, "📚 شارك موقعك لرؤية الكتب القريبة منك");
  s = s.replace(/^(\d+) (book|books) available within (\d+) km of you$/, "$1 كتاب متاح ضمن $3 كم منك");
  s = s.replace(/^(\d+) listed$/, "$1 معروض");
  s = s.replace(/^by (.+)$/, "بقلم $1");
  s = s.replace(/^(\d+(?:\.\d+)?) km away$/, "$1 كم بعيدًا");
  s = s.replace(/^(\d+(?:\.\d+)?) km$/, "$1 كم");
  s = s.replace(/^(\d+) books$/, "$1 كتاب");
  s = s.replace(/^(\d+) followers$/, "$1 متابع");
  s = s.replace(/^(\d+) following$/, "$1 يتابع");
  s = s.replace(/^Status: (.+)$/, (_, status) => `الحالة: ${optionTranslations[status] ?? status}`);
  s = s.replace(/^(\d+) (strike|strikes) on this account$/, "$1 مخالفة على هذا الحساب");
  s = s.replace(/^Requests \((\d+)\)$/, "الطلبات ($1)");
  return s;
}

export function translateOption(value: string, lang: Lang): string {
  return translateStaticText(value, lang);
}

type I18nCtx = {
  lang: Lang;
  setLang: (l: Lang) => void;
  t: (key: string, vars?: Record<string, string | number>) => string;
  dir: "ltr" | "rtl";
};

const Ctx = createContext<I18nCtx>({
  lang: "en",
  setLang: () => {},
  t: (k) => k,
  dir: "ltr",
});

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>("en");

  useEffect(() => {
    if (typeof window === "undefined") return;
    const stored = localStorage.getItem(KEY) as Lang | null;
    if (stored === "en" || stored === "ar") setLangState(stored);
  }, []);

  useEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
  }, [lang]);

  const setLang = (l: Lang) => {
    setLangState(l);
    if (typeof window !== "undefined") localStorage.setItem(KEY, l);
  };

  const t = (key: string, vars?: Record<string, string | number>) => {
    let s = dicts[lang][key] ?? dicts.en[key] ?? key;
    if (vars) for (const [k, v] of Object.entries(vars)) s = s.replace(`{${k}}`, String(v));
    return s;
  };

  return (
    <Ctx.Provider value={{ lang, setLang, t, dir: lang === "ar" ? "rtl" : "ltr" }}>
      {children}
    </Ctx.Provider>
  );
}

export const useI18n = () => useContext(Ctx);
